import { useTranslation } from "react-i18next";
import { useInboxCampaigns } from "@/lib/inbox";
import { SimpleSelect, type SimpleSelectOption } from "@/components/ui/select";

const ALL = "__all__";
const NONE = "none";

export interface InboxCampaignFilterProps {
  /** A campaign's id, "none" for the answers no campaign started, or null. */
  value: string | null;
  onChange: (campaign: string | null) => void;
  /** Classes for the select itself — the parent lays the filters out. */
  className?: string;
}

/**
 * The campaign filter of the inbox: one campaign's answers. The menu lists the
 * campaigns somebody answered, with how many did.
 *
 * Hidden while there is nothing to choose between — no campaign has an answer
 * yet. A chosen campaign always shows: hidden, it could not be un-chosen.
 */
export function InboxCampaignFilter({ value, onChange, className }: InboxCampaignFilterProps) {
  const { t } = useTranslation();
  const data = useInboxCampaigns().data;
  const campaigns = data?.campaigns ?? [];

  if (campaigns.length === 0 && value === null) return null;

  const options: SimpleSelectOption[] = [
    { value: ALL, label: t("inbox.campaign_all") },
    ...campaigns.map((c) => ({ value: c.id, label: `${c.name} (${c.dialogs})` })),
  ];
  if ((data?.without_campaign ?? 0) > 0 || value === NONE) {
    options.push({
      value: NONE,
      label: `${t("inbox.campaign_none")} (${data?.without_campaign ?? 0})`,
    });
  }

  return (
    <SimpleSelect
      className={className}
      options={options}
      value={value ?? ALL}
      onValueChange={(v) => onChange(v === ALL ? null : v)}
    />
  );
}
InboxCampaignFilter.displayName = "InboxCampaignFilter";
